import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { ReadOnlyButton } from '../common/ReadOnlyButton';

const PermissionSetsTab = ({
  permissionSets,
  selectedPermissionSets,
  onSelectionChange,
  onRemove,
  onPermissionSetClick,
  onRefresh,
  isRefreshing,
  isReadOnly,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter permission sets based on search
  const filteredPermissionSets = permissionSets.filter((ps) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ps.name.toLowerCase().includes(query) ||
      (ps.description && ps.description.toLowerCase().includes(query))
    );
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      onSelectionChange(filteredPermissionSets);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectPermissionSet = (ps) => {
    const isSelected = selectedPermissionSets.some((selected) => selected.id === ps.id);
    if (isSelected) {
      onSelectionChange(selectedPermissionSets.filter((selected) => selected.id !== ps.id));
    } else {
      onSelectionChange([...selectedPermissionSets, ps]);
    }
  };

  const isSelected = (ps) => {
    return selectedPermissionSets.some((selected) => selected.id === ps.id);
  };

  const hasSelections = selectedPermissionSets.length > 0;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffMonths < 12) return `${diffMonths} months ago`;
    return date.toLocaleDateString();
  };

  // Empty state
  if (permissionSets.length === 0) {
    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
            }}
          >
            Permission sets accessing this account (0)
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.875rem',
            color: '#666666',
            mb: 3,
          }}
        >
          Permission sets define the level of access that assigned users and groups in IAM Identity Center have to this AWS account.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            backgroundColor: '#F9FBFD',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              backgroundColor: '#F5EBF8',
              mb: 2,
            }}
          >
            <SecurityIcon sx={{ fontSize: 28, color: '#9B59B6' }} />
          </Avatar>
          <Typography
            sx={{
              color: '#253E66',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              mb: 1,
            }}
          >
            No permission sets assigned
          </Typography>
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              textAlign: 'center',
              maxWidth: 350,
            }}
          >
            Assign users or groups with permission sets to this account
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            color: '#253E66',
          }}
        >
          Permission sets accessing this account ({permissionSets.length})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReadOnlyButton
            variant="outlined"
            onClick={onRemove}
            disabled={isReadOnly || !hasSelections}
            sx={{
              borderColor: hasSelections ? '#E5E5E5' : '#F0F0F0',
              color: hasSelections ? '#253E66' : '#BDBDBD',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#E74C3C',
                color: '#E74C3C',
                backgroundColor: '#FDF2F2',
              },
            }}
          >
            Remove
          </ReadOnlyButton>
          <IconButton
            onClick={onRefresh}
            disabled={isRefreshing}
            sx={{
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#EBF5FB',
                borderColor: '#4698D3',
              },
            }}
          >
            <RefreshIcon
              sx={{
                color: '#4698D3',
                fontSize: 20,
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        sx={{
          fontFamily: 'Metropolis, sans-serif',
          fontSize: '0.875rem',
          color: '#666666',
          mb: 2,
        }}
      >
        Permission sets define the level of access that assigned users and groups in IAM Identity Center have to this AWS account. Permission sets are stored in your Identity Center directory and appear in this account as IAM roles.
      </Typography>

      {/* Search */}
      <TextField
        placeholder="Search permission sets by name or description"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        fullWidth
        sx={{
          mb: 3,
          maxWidth: 500,
          '& .MuiOutlinedInput-root': {
            fontFamily: 'Metropolis, sans-serif',
            backgroundColor: '#FFFFFF',
            borderRadius: '6px',
            '& fieldset': { borderColor: '#E5E5E5' },
            '&:hover fieldset': { borderColor: '#4698D3' },
            '&.Mui-focused fieldset': { borderColor: '#4698D3' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      <TableContainer
        sx={{
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
              <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5', width: 50 }}>
                <Checkbox
                  checked={
                    filteredPermissionSets.length > 0 &&
                    filteredPermissionSets.every((ps) => isSelected(ps))
                  }
                  indeterminate={
                    selectedPermissionSets.length > 0 &&
                    !filteredPermissionSets.every((ps) => isSelected(ps))
                  }
                  onChange={handleSelectAll}
                  sx={{
                    color: '#E5E5E5',
                    '&.Mui-checked': { color: '#4698D3' },
                    '&.MuiCheckbox-indeterminate': { color: '#4698D3' },
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#253E66',
                  borderBottom: '1px solid #E5E5E5',
                }}
              >
                Permission set
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#253E66',
                  borderBottom: '1px solid #E5E5E5',
                }}
              >
                Description
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPermissionSets.map((ps) => {
              const selected = isSelected(ps);

              return (
                <TableRow
                  key={ps.id}
                  hover
                  sx={{
                    backgroundColor: selected ? '#EBF5FB' : 'transparent',
                    '&:hover': {
                      backgroundColor: selected ? '#EBF5FB' : '#F9FBFD',
                    },
                    '&:last-child td': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Checkbox
                      checked={selected}
                      onChange={() => handleSelectPermissionSet(ps)}
                      sx={{
                        color: '#E5E5E5',
                        '&.Mui-checked': { color: '#4698D3' },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        onClick={() => onPermissionSetClick(ps.id)}
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          color: '#4698D3',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationColor: 'transparent',
                          '&:hover': {
                            textDecorationColor: '#4698D3',
                          },
                        }}
                      >
                        {ps.name}
                      </Typography>
                      {ps.hasJIT && (
                        <Tooltip
                          title={`${ps.jitCount} JIT assignment${ps.jitCount > 1 ? 's' : ''} (temporary access)`}
                          arrow
                        >
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                            label={`JIT${ps.jitCount > 1 ? ` (${ps.jitCount})` : ''}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              backgroundColor: '#F5EBF8',
                              color: '#9B59B6',
                              border: '1px solid #9B59B6',
                              '& .MuiChip-icon': {
                                color: '#9B59B6',
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.875rem',
                        color: '#666666',
                      }}
                    >
                      {ps.description || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* No search results */}
      {filteredPermissionSets.length === 0 && searchQuery && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.95rem',
            }}
          >
            No results match "{searchQuery}"
          </Typography>
        </Box>
      )}
    </Box>
    </motion.div>
  );
};

export default PermissionSetsTab;
